import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import onhireService from '../../services/OnHireService.js';
import tankService from '../../services/TankService.js';
import userService from '../../services/UserService.js';
import locationService from '../../services/LocationService.js';
import { Package, ChevronDown, ChevronUp, ImageIcon, Info, Calendar, Database, Settings, ShieldAlert, ZapOff, Puzzle, ArrowLeft } from "lucide-react";
import { useAuth } from '../../context/AuthContext.js';
import TankCanvasModal from './TankCanvasModal.js';
import DocumentModal from '../common/DocumentModal.js';
import { shouldLockInspectorInAdd } from '../../utils/roleBasedUIHelpers.js';
import { ROLES } from '../../utils/permissions.js';

// dropdown component (MOVED OUTSIDE to prevent re-creation)
const CustomDropdownField = ({ label, fieldName, ringColor = 'blue', formData, customOptions, showAddOption, newOptionText, handleDropdownChange, setNewOptionText, handleAddCustomOption, setShowAddOption }) => (
  <div>
    <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
      {label}
    </label>
    <select
      name={fieldName}
      value={formData[fieldName] || ''}
      onChange={(e) => handleDropdownChange(e, fieldName)}
      className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-${ringColor}-500 text-sm`}
    >
      <option value="">Select</option>
      {customOptions[fieldName]?.map((option, index) => (
        <option key={index} value={option}>
          {option}
        </option>
      ))}
      <option value="__add_new__">+ Add Other</option>
    </select>
    
    {showAddOption[fieldName] && (
      <div className="mt-2 flex gap-2">
        <input
          type="text"
          value={newOptionText[fieldName] || ''}
          onChange={(e) => setNewOptionText(prev => ({
            ...prev,
            [fieldName]: e.target.value
          }))}
          placeholder="Enter new option"
          className={`flex-1 px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-${ringColor}-500 text-sm`}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddCustomOption(fieldName);
            }
          }}
        />
        <button
          type="button"
          onClick={() => handleAddCustomOption(fieldName)}
          className={`px-4 py-2 bg-${ringColor}-500 text-white rounded-sm hover:bg-${ringColor}-600 text-sm font-medium`}
        >
          Add
        </button>
        <button
          type="button"
          onClick={() => setShowAddOption(prev => ({
            ...prev,
            [fieldName]: false
          }))}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-sm hover:bg-gray-400 text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    )}
  </div>
);

  // MM/YYYY + By: input — defined OUTSIDE AddOnHire to prevent remount on every keystroke
  const MonthYearByInput = ({ label, fieldName, value, onChange }) => {
    const match = (value || '').match(/^(\d{2}\/\d{4})\s+By:\s*(.*)$/);
    const mmyyyy = match ? match[1] : '';

    // Local state for byVal so typing is never blocked by a missing date
    const [localBy, setLocalBy] = React.useState(match ? match[2] : '');

    // Keep localBy in sync if value changes externally (e.g. load existing record)
    React.useEffect(() => {
      const m = (value || '').match(/^(\d{2}\/\d{4})\s+By:\s*(.*)$/);
      setLocalBy(m ? m[2] : '');
    }, [fieldName]); // only sync on field identity change, not every keystroke

    const toInputVal = () => {
      if (!mmyyyy) return '';
      const [mm, yyyy] = mmyyyy.split('/');
      return `${yyyy}-${mm}`;
    };
    const handleMonth = (e) => {
      if (!e.target.value) { onChange(''); return; }
      const [yyyy, mm] = e.target.value.split('-');
      onChange(`${mm}/${yyyy}     By: ${localBy}`);
    };
    const handleBy = (e) => {
      setLocalBy(e.target.value);
      onChange(`${mmyyyy}     By: ${e.target.value}`);
    };
    return (
      <div>
        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">{label}</label>
        <div className="flex gap-2 items-center">
          <input type="month" value={toInputVal()} onChange={handleMonth}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
          <span className="text-xs text-gray-500 whitespace-nowrap font-medium">By:</span>
          <input type="text" value={localBy} onChange={handleBy} placeholder="Name"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
        </div>
      </div>
    );
  };

// MM/YYYY only — defined OUTSIDE AddOnHire to prevent remount on every keystroke
const MonthYearInput = ({ label, fieldName, value, onChange }) => {
  const toInputVal = (stored) => {
    if (!stored) return '';
    const parts = stored.split('/');
    const mm = parts[0];
    const yyyy = parts[1];
    if (!mm || !yyyy || yyyy.length !== 4) return '';
    return `${yyyy}-${mm}`;
  };

  const [localVal, setLocalVal] = React.useState(toInputVal(value));

  React.useEffect(() => {
    setLocalVal(toInputVal(value));
  }, [fieldName]);

  const handleMonth = (e) => {
    const val = e.target.value;
    setLocalVal(val);
    if (!val) { onChange(''); return; }
    const [yyyy, mm] = val.split('-');
    if (yyyy && yyyy.length === 4 && mm) {
      onChange(`${mm}/${yyyy}`);
    }
  };

  return (
    <div>
      <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">{label}</label>
      <input
        type="month"
        value={localVal}
        onChange={handleMonth}
        className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
    </div>
  );
};

const AddOnHire = () => {
  const [canvasImages, setCanvasImages] = useState({
    type1: null,
    type2: null
  });
  
  const [formData, setFormData] = useState({
    client_id: '',
    tank_id: '',
    location_id: '',
    date_of_survey: new Date().toISOString().split('T')[0],
    inspected_by_id: '',
    comments: '',
    remarks: '',
    published: false,
    send_to_dm: true,
    
    // Tank data fields (auto-populated, but editable)
    tank_no: '',
    tank_type: 'T11',
    manufacturer: '',
    manufacture_date: '',
    iso_type: '',
    imo_class: '1',
    un_number: '',
    serial_number: '',
    size: '',
    maximum_gross_weight: '36000',
    tare_weight: '',
    capacity_ltr: '',
    mawp: '',
    test_pressure: '',
    csc: '',
    cleaning_corporation: '',
    depot_name: '',
    steam_tube_wp: '',
    steam_test_pressure: '',
    shell_material: '',
    shell_thikness: '',
    head_material: '',
    head_thikness: '',
    led: '',
    ned: '',
    first_test_date: '',
    last_2_5_year_test_date: '',
    last_5_year_test_date: '',
    next_test_date: '',
    form_of_liquid_tank: 'Chemicals',
    survey_type: '',
    
    // VALVES Section
    bottom_discharge_fort_valve: '',
    bottom_discharge_sn: '',
    bottom_discharge_butterfly_valve: '',
    bottom_discharge_spacers: '',
    bottom_discharge_highlift: '',
    bottom_discharge_tir_strips: 'No',
    footvalve_fort_valve: '',
    footvalve_tir_strip: 'No',
    bottom_outlet_connection_type: '',
    bottom_outlet_cap_blank: '',
    bottom_outlet_cap: '',
    remote_trip: '',
    top_discharge: '',
    top_discharge_manufacture: '',
    top_discharge_type: '',
    top_discharge_tir_strip: 'No',
    siphon_tube: '',
    top_loading: '',
    top_loading_type: '',
    top_loading_manufacture: '',
    top_loading_flanged: '',
    top_loading_tir_strips: 'No',
    air_vent_type: '',
    air_vent_connection: '',
    air_vent_connection_tir_strips: 'No',
    air_vent_cap_blank: '',
    airline_pressure_gauge: '',
    
    // SRV 1
    srv1_type: '',
    srv1_serial_no: '',
    srv1_tir_strips: 'No',
    srv1_vacuum: '',
    srv1_pressure: '',
    srv1_flame_screen: 'No',
    
    // SRV 2
    srv2_type: '',
    srv2_serial_no: '',
    srv2_tir_strips: 'No',
    srv2_vacuum: '',
    srv2_pressure: '',
    srv2_flame_screen: 'No',
    
    // Rupture Disc 1
    rupture_disc1_manufacturer: '',
    rupture_disc1_pressure: '',
    rupture_disc1_manometer: 'No',
    rupture_disc1_size: '',
    
    // Rupture Disc 2
    rupture_disc2_manufacturer: '',
    rupture_disc2_pressure: '',
    rupture_disc2_manometer: 'No',
    rupture_disc2_size: '',
    
    // ANCILLARY FITTINGS Section
    cladding_type: '',
    ladders_type: '',
    placard_holders: '',
    decals_type: '',
    temp_gauge_type: '',
    temp_gauge_notes: '',
    document_tube: 'No',
    steam_tube_cap: '',
    steam_accessories: '',
    bottom_compartment_cover: '',
    electrical_heating_cooling: 'No',
    calibration_table: '',
    calibration_table_tank_no: '',
    manlid_type: '',
    collapsable_handrails: 'No',  
    dipstick: '',
    topcovers_lockings: '',
    walkway_type: '',
    manlid_gasket: '',
    grounding_lug: 'No',
    bottom_plate: 'No',
    frame_cladding_details: '',
  });

  // Custom options state for specific fields
  const [customOptions, setCustomOptions] = useState({
    bottom_discharge_fort_valve: ['Fortvale', 'Perolo', 'Pelican'],
    bottom_discharge_butterfly_valve: ['Butterfly', 'Ball'],
    footvalve_fort_valve: ['Fortvale', 'Perolo', 'Pelican'],
    bottom_discharge_highlift: [ 'Fortvale Highlift', 'Fortvale Cleanflow', 'Perelo Netflow', 'Perelo Neatco'],
    remote_trip: ['Pull cable', 'Handle', 'Spring', 'Fusible link'],
    air_vent_connection: ['BSP', 'NPT', 'Quick', 'Flange'],
    cladding_type: ['SS', 'ALU', 'GRP'],
    ladders_type: ['IRON', 'SS', 'ALU', 'Galvanized'],
    calibration_table: ['Stainless Steel', 'Paper', 'In Spillbox', 'In Cover', 'On Cladding'],
    dipstick: ['Angle', 'Flat', 'Legible', 'Bracket'],
    topcovers_lockings: ['SS', 'ALU', 'IRON'],
    manlid_gasket: ['PTFE', 'SWR', 'Viton A', 'Super Tanktyt'],
    steam_tube_cap: ['BSP', 'SS', 'PVC', 'ALU', 'BRASS'],
    steam_accessories: ['Drainvalve', 'PV'],
    bottom_compartment_cover: ['SS', 'ALU', 'IRON'],
    decals_type: ['Weight', 'Capacity', 'Food Logos'],
    temp_gauge_type: ['Manufacturing', 'Temp', 'Gauge'],
    bottom_outlet_cap_blank: ['4 Bolt Blank', '6 Bolt Blank', '8 Bolt Blank'],
    bottom_outlet_cap: ['CC', 'ALU', 'PVC', 'BRASS'],
    top_discharge: ['4 Bolt Blank', '6 Bolt Blank', '8 Bolt Blank'],
    top_discharge_manufacture: ['Fortvale', 'Perolo', 'Pelican'],
    top_discharge_type: ['Ball Valve', 'Butterfly'],
    top_loading: ['4 Bolt Blank', '6 Bolt Blank', '8 Bolt Blank'],
    top_loading_manufacture: ['Fortvale', 'Perolo', 'Pelican'],
    top_loading_type: ['Ball Valve', 'Butterfly'],
    form_of_liquid_tank: ['Food', 'Chemicals'],
  });

  const [newOptionText, setNewOptionText] = useState({
    bottom_discharge_fort_valve: '',
    bottom_discharge_butterfly_valve: '',
    footvalve_fort_valve: '',
    bottom_discharge_highlift: '',
    remote_trip: '',
    air_vent_connection: '',
    cladding_type: '',
    ladders_type: '',
    calibration_table: '',
    dipstick: '',
    topcovers_lockings: '',
    manlid_gasket: '',
    steam_tube_cap: '',
    steam_accessories: '',
    decals_type: '',
    temp_gauge_type: '',
    bottom_outlet_cap_blank: '',
    bottom_outlet_cap: '',
    top_discharge: '',
    top_discharge_manufacture: '',
    top_discharge_type: '',
    top_loading: '',
    top_loading_manufacture: '',
    top_loading_type: '',
    form_of_liquid_tank: '',
  });

  const [showAddOption, setShowAddOption] = useState({
    bottom_discharge_fort_valve: false,
    bottom_discharge_butterfly_valve: false,
    footvalve_fort_valve: false,
    bottom_discharge_highlift: false,
    remote_trip: false,
    air_vent_connection: false,
    cladding_type: false,
    ladders_type: false,
    calibration_table: false,
    dipstick: false,
    topcovers_lockings: false,
    manlid_gasket: false,
    steam_tube_cap: false,
    steam_accessories: false,
    decals_type: false,
    temp_gauge_type: false,
    bottom_outlet_cap_blank: false,
    bottom_outlet_cap: false,
    top_discharge: false,
    top_discharge_manufacture: false,
    top_discharge_type: false,
    top_loading: false,
    top_loading_manufacture: false,
    top_loading_type: false,
    form_of_liquid_tank: false,
  });

  const [clients, setClients] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [useExistingTank, setUseExistingTank] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    canvas: true,
    tankDetails: true,
    valves: false,
    srv: false,
    ruptureDisc: false,
    ancillary: false,
  });
  const [canvasModalOpen, setCanvasModalOpen] = useState(false);
  const [activeCanvasType, setActiveCanvasType] = useState(null);
  const [savedCanvases, setSavedCanvases] = useState({});

  // DocumentModal state for viewing canvas images
  const [documentModal, setDocumentModal] = useState({
    isOpen: false,
    url: null,
    type: null,
  });

  const { user } = useAuth();
  const isSurveyInspector = user?.role === 'Survey Inspector' || user?.role_name === 'Survey Inspector';
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchInspectors();
    fetchLocations();
    
    // Auto-fill inspector field for Survey Inspector role
    if (isSurveyInspector && user?.user_id) {
      setFormData(prev => ({
        ...prev,
        inspected_by_id: user.user_id
      }));
    }
  }, [isSurveyInspector, user?.user_id]);

  useEffect(() => {
    if (useExistingTank && formData.client_id) {
      fetchClientTanks(formData.client_id);
    } else {
      setTanks([]);
      setFormData(prev => ({ ...prev, tank_id: '' }));
    }
  }, [formData.client_id, useExistingTank]);

  useEffect(() => {
    if (useExistingTank && formData.tank_id) {
      loadTankData(formData.tank_id);
    }
  }, [formData.tank_id, useExistingTank]);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      Alert.error('Failed to load clients');
      console.error(err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      Alert.error('Failed to load locations');
      console.error(err);
    }
  };

  const fetchClientTanks = async (clientId) => {
    try {
      const response = await tankService.getByClientId(clientId);
      setTanks(response.data || []);
    } catch (err) {
      Alert.error('Failed to load tanks');
      console.error(err);
    }
  };

  const fetchInspectors = async () => {
    try {
      const response = await userService.getByRole(4);
      setInspectors(response.data || []);
    } catch (err) {
      console.error('Failed to load inspectors:', err);
    }
  };

  // Yes/No Dropdown Component for TIR Strips
  const YesNoDropdown = ({ label, name, value, onChange, focusRing = "blue" }) => {
    return (
      <div>
        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
          {label}
        </label>
        <select
          name={name}
          value={value || 'No'}
          onChange={onChange}
          className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-${focusRing}-500 text-sm bg-white`}
        >
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </select>
      </div>
    );
  };

  const loadTankData = async (tankId) => {
    try {
      const response = await tankService.getById(tankId);
      const tank = response.data;
      
      setFormData(prev => ({
        ...prev,
        tank_no: tank.tank_no || '',
        tank_type: tank.tank_type || '',
        manufacturer: tank.manufacturer || '',
        manufacture_date: tank.manufacture_date || '',
        iso_type: tank.iso_type || '',
        imo_class: tank.imo_class || '',
        un_number: tank.un_number || '',
        serial_number: tank.serial_number || '',
        size: tank.size || '',
        maximum_gross_weight: tank.maximum_gross_weight || '',
        tare_weight: tank.tare_weight || '',
        capacity_ltr: tank.capacity_ltr || '',
        mawp: tank.mawp || '',
        test_pressure: tank.test_pressure || '',
        csc: tank.csc || '',
        cleaning_corporation: tank.cleaning_corporation || '',
        depot_name: tank.depot_name || '',
        steam_tube_wp: tank.steam_tube_wp || '',
        steam_test_pressure: tank.steam_test_pressure || '',
        shell_material: tank.shell_material || '',
        shell_thikness: tank.shell_thikness || '',
        head_material: tank.head_material || '',
        head_thikness: tank.head_thikness || '',
        led: tank.led || '',
        ned: tank.ned || '',
      }));
    } catch (err) {
      Alert.error('Failed to load tank data');
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'led' && value) {
      const ledDate = new Date(value);
      ledDate.setFullYear(ledDate.getFullYear() + 2);
      ledDate.setMonth(ledDate.getMonth() + 6);
      const nedValue = ledDate.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        led: value,
        ned: nedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleUseExistingTankToggle = (e) => {
    const checked = e.target.checked;
    setUseExistingTank(checked);

    if (checked) {
      if (formData.client_id) {
        fetchClientTanks(formData.client_id);
      }
    } else {
      setTanks([]);
      setFormData(prev => ({
        ...prev,
        tank_id: '',
        tank_no: '',
        tank_type: '',
        manufacturer: '',
        manufacture_date: '',
        iso_type: '',
        imo_class: '',
        un_number: '',
        serial_number: '',
        size: '',
        maximum_gross_weight: '',
        tare_weight: '',
        capacity_ltr: '',
        mawp: '',
        test_pressure: '',
        csc: '',
        cleaning_corporation: '',
        depot_name: '',
        steam_tube_wp: '',
        steam_test_pressure: '',
        shell_material: '',
        shell_thikness: '',
        head_material: '',
        head_thikness: '',
        led: '',
        ned: ''
      }));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCanvasOpen = (type) => {
    setActiveCanvasType(type);
    setCanvasModalOpen(true);
  };

  const handleCanvasSave = (data) => {
    console.log('Canvas saved:', data);
    
    // Update local state - this will be submitted with form
    setCanvasImages(prev => ({
      ...prev,
      [data.type]: data
    }));
    
    // Update savedCanvases to show button state
    setSavedCanvases(prev => ({
      ...prev,
      [data.type]: true
    }));
    
    setCanvasModalOpen(false);
  };

  // Open DocumentModal to view the saved canvas image
  const handleCanvasViewImage = (type) => {
    const canvasData = canvasImages[type];
    if (canvasData && canvasData.imageData) {
      setDocumentModal({
        isOpen: true,
        url: canvasData.imageData,
        type: type === 'type1' ? 'tank_views_front_rear' : 'tank_sections',
      });
    }
  };

  const handleCloseDocumentModal = () => {
    setDocumentModal({ isOpen: false, url: null, type: null });
  };

  // Handler for adding custom option to dropdown
  const handleAddCustomOption = (fieldName) => {
    const trimmedValue = newOptionText[fieldName]?.trim();
    if (trimmedValue && !customOptions[fieldName].includes(trimmedValue)) {
      setCustomOptions(prev => ({
        ...prev,
        [fieldName]: [...prev[fieldName], trimmedValue]
      }));
      setFormData(prev => ({
        ...prev,
        [fieldName]: trimmedValue
      }));
      setNewOptionText(prev => ({
        ...prev,
        [fieldName]: ''
      }));
      setShowAddOption(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
  };

  // Handler for dropdown change
  const handleDropdownChange = (e, fieldName) => {
    const value = e.target.value;
    if (value === '__add_new__') {
      setShowAddOption(prev => ({
        ...prev,
        [fieldName]: true
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));
      setShowAddOption(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const cleanedData = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') {
          cleanedData[key] = null;
        }
      });

      const img1Data = canvasImages.type1?.imageData;
      const img2Data = canvasImages.type2?.imageData;

      if (img1Data) {
        cleanedData.image_1 = img1Data;
      }
      if (img2Data) {
        cleanedData.image_2 = img2Data;
      }

      // Append units before saving — not shown on page
      const kgFields = ['maximum_gross_weight', 'tare_weight'];
      const ltrFields = ['capacity_ltr'];
      const barFields = ['mawp', 'test_pressure', 'steam_tube_wp', 'steam_test_pressure', 'srv1_pressure', 'srv2_pressure', 'rupture_disc1_pressure', 'rupture_disc2_pressure'];

      kgFields.forEach(f => {
        if (cleanedData[f] && !String(cleanedData[f]).endsWith('KG')) cleanedData[f] = `${cleanedData[f]} KG`;
      });
      ltrFields.forEach(f => {
        if (cleanedData[f] && !String(cleanedData[f]).endsWith('Ltr')) cleanedData[f] = `${cleanedData[f]} Ltr`;
      });
      barFields.forEach(f => {
        if (cleanedData[f] && !String(cleanedData[f]).endsWith('bar')) cleanedData[f] = `${cleanedData[f]} bar`;
      });

      await onhireService.create(cleanedData);
      Alert.success('OnHire record created successfully!');
      
      setTimeout(() => navigate('/onhires'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create onhire record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <TankCanvasModal 
        isOpen={canvasModalOpen} 
        onClose={() => setCanvasModalOpen(false)}
        imageType={activeCanvasType}
        onSave={handleCanvasSave}
        existingImage={activeCanvasType ? canvasImages[activeCanvasType] : null}
      />

      {/* Document Modal for viewing canvas images */}
      <DocumentModal
        isOpen={documentModal.isOpen}
        onClose={handleCloseDocumentModal}
        documentUrl={documentModal.url}
        documentType={documentModal.type}
      />

      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Package className="w-6 h-6 text-buttonBg" /> Add OnHire Record
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/onhires')}
                    className="relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg">
                    {/* COLLAPSING BACKGROUND */}
                    <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0"/>
                    {/* ICON */}
                    <ArrowLeft className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90"/>
                    <span className="relative z-10">Back to Dry Box Survey List</span>
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  
                  {/* TANK INSPECTION CANVAS Section */}
                  <div className="border border-gray-200 rounded-sm">
                    <button
                      type="button"
                      onClick={() => toggleSection('canvas')}
                      className="
                          w-full
                          px-4 sm:px-6 py-3 sm:py-4
                          bg-gray-100
                          text-gray-700
                          font-semibold
                          flex items-center justify-between

                          border-b border-gray-100
                          hover:bg-gray-50
                          transition-colors duration-200
                        "
                      >
                      <span className="flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-buttonBg" /> Tank Inspection Images
                      </span>
                      {expandedSections.canvas ? <ChevronUp className="w-5 h-5 text-buttonBg" /> : <ChevronDown className="w-5 h-5 text-buttonBg" />}
                    </button>
                    
                    {expandedSections.canvas && (
                      <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          Edit and mark tank inspection images with inspection symbols. You can add marks for defects, repairs, and conditions.
                        </p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Canvas Type 1 */}
                          <div className="border border-gray-300 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-0">
                              <div>
                                <h4 className="font-semibold text-secondary">Tank Views (Front & Rear)</h4>
                                <p className="text-xs text-gray-500 mt-1">Type 1 - Standard tank inspection diagram</p>
                              </div>
                              <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleCanvasOpen('type1')}
                                className="flex-1 px-4 py-2 bg-buttonBg hover:bg-buttonBg text-white rounded-lg font-medium text-sm transition-colors"
                              >
                                {savedCanvases.type1 ? '✓ Edit' : 'Create'}
                              </button>
                              {savedCanvases.type1 && (
                                <button
                                  type="button"
                                  onClick={() => handleCanvasViewImage('type1')}
                                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors"
                                >
                                  View
                                </button>
                              )}
                            </div>
                            </div>
                          </div>

                          {/* Canvas Type 2 */}
                          <div className="border border-gray-300 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-0">
                              <div>
                                <h4 className="font-semibold text-secondary">Tank Sections</h4>
                                <p className="text-xs text-gray-500 mt-1">Type 2 - Detailed section views (6 views)</p>
                              </div>
                              <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleCanvasOpen('type2')}
                                className="flex-1 px-4 py-2 bg-buttonBg hover:bg-buttonBg text-white rounded-lg font-medium text-sm transition-colors"
                              >
                                {savedCanvases.type2 ? '✓ Edit' : 'Create'}
                              </button>
                              {savedCanvases.type2 && (
                                <button
                                  type="button"
                                  onClick={() => handleCanvasViewImage('type2')}
                                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors"
                                >
                                  View
                                </button>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>

                        {(savedCanvases.type1 || savedCanvases.type2) && (
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                            <strong>✓ Canvas images saved:</strong> {Object.keys(savedCanvases).length} image(s) ready. They will be included in your submission.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* BASIC INFORMATION Section */}
                  <div className="border border-gray-200 rounded-sm">
                    <button
                      type="button"
                      onClick={() => toggleSection('basic')}
                      className="
                        w-full
                        px-4 sm:px-6 py-3 sm:py-4
                        bg-gray-100
                        text-gray-700
                        font-semibold
                        flex items-center justify-between
                        border-b border-gray-100
                        hover:bg-gray-50
                        transition-colors duration-200
                        "
                    >
                      <span className="flex items-center gap-2 uppercase">
                        <Info className="w-5 h-5 text-buttonBg" />
                        Basic Information
                      </span>
                      {expandedSections.basic ? <ChevronUp className="w-5 h-5 text-buttonBg" /> : <ChevronDown className="w-5 h-5 text-buttonBg" />}
                    </button>
                    
                    {expandedSections.basic && (
                      <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                              Client <span className="text-danger font-bold">*</span>
                            </label>
                            <select
                              name="client_id"
                              value={formData.client_id}
                              onChange={handleChange}
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                              required
                            >
                              <option value="">Select Client</option>
                              {clients.map((client) => (
                                <option key={client.client_id} value={client.client_id}>
                                  {client.name} - {client.organization_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <input
                                type="checkbox"
                                id="use_existing_tank"
                                checked={useExistingTank}
                                onChange={handleUseExistingTankToggle}
                                className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                disabled={!formData.client_id}
                              />
                              <label htmlFor="use_existing_tank" className="text-xs sm:text-sm font-medium text-secondary">
                                Use Existing Tank
                              </label>
                            </div>
                            {useExistingTank && (
                              <select
                                name="tank_id"
                                value={formData.tank_id}
                                onChange={handleChange}
                                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                                required={useExistingTank}
                                disabled={!formData.client_id}
                              >
                                <option value="">Select Tank</option>
                                {tanks.map((tank) => (
                                  <option key={tank.id} value={tank.id}>
                                    {tank.tank_no || `Tank ${tank.id}`}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                              Location <span className="text-danger font-bold">*</span>
                            </label>
                            <select
                              name="location_id"
                              value={formData.location_id}
                              onChange={handleChange}
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                              required
                            >
                              <option value="">Select Location</option>
                              {locations.map((l) => (
                                <option key={l.id} value={l.id}>{l.city}, {l.state} ({l.city_code})</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                              Date of Survey <span className="text-danger font-bold">*</span>
                            </label>
                            <input
                              type="date"
                              name="date_of_survey"
                              value={formData.date_of_survey}
                              onChange={handleChange}
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                              Inspected By
                              {isSurveyInspector && <span className="text-blue-600 ml-2">(Auto-filled)</span>}
                            </label>
                            <select
                              name="inspected_by_id"
                              value={formData.inspected_by_id}
                              onChange={handleChange}
                              disabled={isSurveyInspector}
                              className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm ${isSurveyInspector ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            >
                              <option value="">Select Inspector</option>
                              {inspectors.map((inspector) => (
                                <option key={inspector.id} value={inspector.user_id}>{inspector.name}{isSurveyInspector && inspector.user_id === user?.user_id ? ' (You)' : ''} - {inspector.email}</option>
                              ))}
                            </select>
                          </div>
                          <CustomDropdownField label="Form of Liquid Tank" fieldName="form_of_liquid_tank" ringColor="blue" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Survey Type</label>
                            <select
                              name="survey_type"
                              value={formData.survey_type || ''}
                              onChange={handleChange}
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="ON-HIRE">ON-HIRE</option>
                              <option value="OFF-HIRE">OFF-HIRE</option>
                              <option value="CONDITIONAL">CONDITIONAL</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Comments</label>
                            <textarea name="comments" value={formData.comments} onChange={handleChange} rows="3" className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Remarks</label>
                            <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="3" className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" />
                          </div>
                        </div>

                        <div className="flex gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" name="published" checked={formData.published} onChange={handleChange} className="w-4 h-4 accent-buttonBg" />
                            <span className="text-xs sm:text-sm font-medium text-secondary">Published</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" name="send_to_dm" checked={formData.send_to_dm} onChange={handleChange} className="w-4 h-4 accent-buttonBg" />
                            <span className="text-xs sm:text-sm font-medium text-secondary">Send to DM</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* TANK DETAILS Section */}
                  <div className="border border-gray-200 rounded-sm">
                    <button
                      type="button"
                      onClick={() => toggleSection('tankDetails')}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 text-gray-700 font-semibold flex items-center justify-between border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="flex items-center gap-2 uppercase">
                        <Database className="w-5 h-5 text-buttonBg" /> Tank Details
                      </span>
                      {expandedSections.tankDetails ? <ChevronUp className="w-5 h-5 text-buttonBg" /> : <ChevronDown className="w-5 h-5 text-buttonBg" />}
                    </button>
                    
                    {expandedSections.tankDetails && (
                      <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Tank No <span className="text-danger font-bold">*</span></label><input type="text" name="tank_no" value={formData.tank_no} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">ISO Type</label><input type="text" name="iso_type" value={formData.iso_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Tank Type</label><input type="text" name="tank_type" value={formData.tank_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">IMO Class</label><input type="text" name="imo_class" value={formData.imo_class} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div> */}
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Manufacturer</label><input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Manufacture Date</label><div className='relative w-full'><input type="date" name="manufacture_date" value={formData.manufacture_date} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /><Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/></div></div> */}
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">UN Number</label><input type="text" name="un_number" value={formData.un_number} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div> */}
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Serial Number</label><input type="text" name="serial_number" value={formData.serial_number} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">CSC</label><input type="text" name="csc" value={formData.csc} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div> */}
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Cleaning Corporation</label><input type="text" name="cleaning_corporation" value={formData.cleaning_corporation} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div> */}
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Depot Name</label><input type="text" name="depot_name" value={formData.depot_name} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Maximum Gross Weight (Kg)</label><input type="text" name="maximum_gross_weight" value={formData.maximum_gross_weight} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Tare Weight (Kg)</label><input type="text" name="tare_weight" value={formData.tare_weight} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Capacity (Ltr)</label><input type="text" name="capacity_ltr" value={formData.capacity_ltr} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Size (Feet)</label><input type="text" name="size" value={formData.size} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div> */}
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">MAWP (Bar)</label><input type="text" name="mawp" value={formData.mawp} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Test Pressure (Bar)</label><input type="text" name="test_pressure" value={formData.test_pressure} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Steam Tube WP (Bar)</label><input type="text" name="steam_tube_wp" value={formData.steam_tube_wp} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Steam Test Pressure (Bar)</label><input type="text" name="steam_test_pressure" value={formData.steam_test_pressure} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Shell Material</label><input type="text" name="shell_material" value={formData.shell_material} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Shell Thickness</label><input type="text" name="shell_thikness" value={formData.shell_thikness} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Head Material</label><input type="text" name="head_material" value={formData.head_material} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Head Thickness</label><input type="text" name="head_thikness" value={formData.head_thikness} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">LED</label><div className='relative w-full'><input type="date" name="led" value={formData.led} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /><Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/></div></div> */}
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">NED</label><div className='relative w-full'><input type="date" name="ned" value={formData.ned} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /><Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/></div></div> */}
                          <MonthYearByInput label="First Test Date" fieldName="first_test_date" value={formData.first_test_date} onChange={(val) => setFormData(prev => ({ ...prev, first_test_date: val }))} />
                          <MonthYearByInput label="Last 2.5 Year Test Date" fieldName="last_2_5_year_test_date" value={formData.last_2_5_year_test_date} onChange={(val) => setFormData(prev => ({ ...prev, last_2_5_year_test_date: val }))} />
                          <MonthYearByInput label="Last 5 Year Test Date" fieldName="last_5_year_test_date" value={formData.last_5_year_test_date} onChange={(val) => setFormData(prev => ({ ...prev, last_5_year_test_date: val }))} />
                          <MonthYearInput label="Next Test Date" fieldName="next_test_date" value={formData.next_test_date} onChange={(val) => setFormData(prev => ({ ...prev, next_test_date: val }))} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* VALVES Section */}
                  <div className="border border-gray-200 rounded-sm">
                    <button
                      type="button"
                      onClick={() => toggleSection('valves')}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 text-gray-700 font-semibold flex items-center justify-between border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="flex items-center gap-2 uppercase">
                        <Settings className="w-5 h-5 text-buttonBg" /> Valves
                      </span>
                      {expandedSections.valves ? <ChevronUp className="w-5 h-5 text-buttonBg" /> : <ChevronDown className="w-5 h-5 text-buttonBg" />}
                    </button>
                    
                    {expandedSections.valves && (
                      <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 border-b pb-2 uppercase'>Bottom Discharge</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <CustomDropdownField label="Bottom Discharge" fieldName="bottom_discharge_fort_valve" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Valve Type" fieldName="bottom_discharge_butterfly_valve" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <YesNoDropdown label="Bottom Discharge TIR Strips" name="bottom_discharge_tir_strips" value={formData.bottom_discharge_tir_strips} onChange={handleChange} focusRing="green" />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Bottom Discharge SN</label><input type="text" name="bottom_discharge_sn" value={formData.bottom_discharge_sn} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Bottom Discharge Spacers</label><input type="text" name="bottom_discharge_spacers" value={formData.bottom_discharge_spacers} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                        </div>
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Foot Valve</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <CustomDropdownField label="Foot Valve" fieldName="footvalve_fort_valve" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Footvalve Type" fieldName="bottom_discharge_highlift" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <YesNoDropdown label="Footvalve TIR Strip" name="footvalve_tir_strip" value={formData.footvalve_tir_strip} onChange={handleChange} focusRing="green" />
                        </div>
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Bottom Outlet</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <CustomDropdownField label="Bottom Outlet Cap/Blank" fieldName="bottom_outlet_cap_blank" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Bottom Outlet Connection Type</label><input type="text" name="bottom_outlet_connection_type" value={formData.bottom_outlet_connection_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                          <CustomDropdownField label="Bottom Outlet Cap" fieldName="bottom_outlet_cap" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Remote Trip" fieldName="remote_trip" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                        </div>
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Top Discharge</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <CustomDropdownField label="Top Discharge" fieldName="top_discharge" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Top Discharge Manufacture" fieldName="top_discharge_manufacture" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Top Discharge Type" fieldName="top_discharge_type" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <YesNoDropdown label="Top Discharge TIR Strip" name="top_discharge_tir_strip" value={formData.top_discharge_tir_strip} onChange={handleChange} focusRing="green" />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Siphon Tube</label><input type="text" name="siphon_tube" value={formData.siphon_tube} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                        </div>
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Top Loading</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <CustomDropdownField label="Top Loading" fieldName="top_loading" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Top Loading Manufacture" fieldName="top_loading_manufacture" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Top Loading Type" fieldName="top_loading_type" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Top Loading Flanged</label><input type="text" name="top_loading_flanged" value={formData.top_loading_flanged} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                          <YesNoDropdown label="Top Loading TIR Strips" name="top_loading_tir_strips" value={formData.top_loading_tir_strips} onChange={handleChange} focusRing="green" />
                        </div>
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Air Vent</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Air Vent</label><input type="text" name="air_vent_type" value={formData.air_vent_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                          <CustomDropdownField label="Air Vent Connection" fieldName="air_vent_connection" ringColor="green" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <YesNoDropdown label="Air Vent TIR Strips" name="air_vent_connection_tir_strips" value={formData.air_vent_connection_tir_strips} onChange={handleChange} focusRing="green" />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Air Vent Cap Blank</label><input type="text" name="air_vent_cap_blank" value={formData.air_vent_cap_blank} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Airline Pressure Gauge</label><input type="text" name="airline_pressure_gauge" value={formData.airline_pressure_gauge} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" /></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SRV Section */}
                  <div className="border border-gray-200 rounded-sm">
                    <button
                      type="button"
                      onClick={() => toggleSection('srv')}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 text-gray-700 font-semibold flex items-center justify-between border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="flex items-center gap-2 uppercase">
                        <ShieldAlert className="w-5 h-5 text-buttonBg uppercase" /> Safety Relief Valves
                      </span>
                      {expandedSections.srv ? <ChevronUp className="w-5 h-5 text-buttonBg" /> : <ChevronDown className="w-5 h-5 text-buttonBg" />}
                    </button>
                    
                    {expandedSections.srv && (
                      <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Safety relief valve 1</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV1 Type</label><input type="text" name="srv1_type" value={formData.srv1_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV1 Serial No</label><input type="text" name="srv1_serial_no" value={formData.srv1_serial_no} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV1 Vacuum</label><input type="text" name="srv1_vacuum" value={formData.srv1_vacuum} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV1 Pressure</label><input type="text" name="srv1_pressure" value={formData.srv1_pressure} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <YesNoDropdown label="SRV1 TIR Strips" name="srv1_tir_strips" value={formData.srv1_tir_strips} onChange={handleChange} focusRing="purple" />
                          <YesNoDropdown label="SRV1 Flame Screen" name="srv1_flame_screen" value={formData.srv1_flame_screen} onChange={handleChange} focusRing="purple" />
                        </div>
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Safety relief valve 2</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV2 Type</label><input type="text" name="srv2_type" value={formData.srv2_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV2 Serial No</label><input type="text" name="srv2_serial_no" value={formData.srv2_serial_no} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV2 Vacuum</label><input type="text" name="srv2_vacuum" value={formData.srv2_vacuum} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">SRV2 Pressure</label><input type="text" name="srv2_pressure" value={formData.srv2_pressure} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" /></div>
                          <YesNoDropdown label="SRV2 TIR Strips" name="srv2_tir_strips" value={formData.srv2_tir_strips} onChange={handleChange} focusRing="purple" />
                          <YesNoDropdown label="SRV2 Flame Screen" name="srv2_flame_screen" value={formData.srv2_flame_screen} onChange={handleChange} focusRing="purple" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Rupture Disc Section */}
                  <div className="border border-gray-200 rounded-sm">
                    <button
                      type="button"
                      onClick={() => toggleSection('ruptureDisc')}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 text-gray-700 font-semibold flex items-center justify-between border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="flex items-center gap-2 uppercase">
                        <ZapOff className="w-5 h-5 text-buttonBg" /> Rupture Disc
                      </span>
                      {expandedSections.ruptureDisc ? <ChevronUp className="w-5 h-5 text-buttonBg" /> : <ChevronDown className="w-5 h-5 text-buttonBg" />}
                    </button>
                    
                    {expandedSections.ruptureDisc && (
                      <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Rupture Disc 1</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Rupture Disc 1 Manufacturer</label><input type="text" name="rupture_disc1_manufacturer" value={formData.rupture_disc1_manufacturer} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Rupture Disc 1 Pressure</label><input type="text" name="rupture_disc1_pressure" value={formData.rupture_disc1_pressure} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm" /></div>
                          <YesNoDropdown label="Rupture Disc 1 Manometer" name="rupture_disc1_manometer" value={formData.rupture_disc1_manometer} onChange={handleChange} focusRing="yellow" />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Rupture Disc 1 Size</label><input type="text" name="rupture_disc1_size" value={formData.rupture_disc1_size} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm" /></div>
                        </div>
                        <h2 className='text-md sm:text-md font-medium text-buttonBg mb-2 mt-2 border-b pb-2 uppercase'>Rupture Disc 2</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Rupture Disc 2 Manufacturer</label><input type="text" name="rupture_disc2_manufacturer" value={formData.rupture_disc2_manufacturer} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm" /></div>
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Rupture Disc 2 Pressure</label><input type="text" name="rupture_disc2_pressure" value={formData.rupture_disc2_pressure} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm" /></div>
                          <YesNoDropdown label="Rupture Disc 2 Manometer" name="rupture_disc2_manometer" value={formData.rupture_disc2_manometer} onChange={handleChange} focusRing="yellow" />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Rupture Disc 2 Size</label><input type="text" name="rupture_disc2_size" value={formData.rupture_disc2_size} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm" /></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ANCILLARY FITTINGS Section */}
                  <div className="border border-gray-200 rounded-sm">
                    <button
                      type="button"
                      onClick={() => toggleSection('ancillary')}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 text-gray-700 font-semibold flex items-center justify-between border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="flex items-center gap-2 uppercase">
                        <Puzzle className="w-5 h-5 text-buttonBg" />
                        Ancillary Fittings
                      </span>
                      {expandedSections.ancillary ? <ChevronUp className="w-5 h-5 text-buttonBg" /> : <ChevronDown className="w-5 h-5 text-buttonBg" />}
                    </button>
                    
                    {expandedSections.ancillary && (
                      <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          <CustomDropdownField label="Cladding Type" fieldName="cladding_type" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Ladders Type" fieldName="ladders_type" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Placard Holders</label><input type="text" name="placard_holders" value={formData.placard_holders} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" /></div>
                          <CustomDropdownField label="Decals Type" fieldName="decals_type" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Temperature Gauge Type" fieldName="temp_gauge_type" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Temperature Gauge Notes</label><input type="text" name="temp_gauge_notes" value={formData.temp_gauge_notes} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" /></div>
                          <YesNoDropdown label="Document Tube" name="document_tube" value={formData.document_tube} onChange={handleChange} focusRing="red" />
                          <CustomDropdownField label="Steam Tube Cap" fieldName="steam_tube_cap" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Steam Accessories" fieldName="steam_accessories" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Bottom Compartment Cover Material" fieldName="bottom_compartment_cover" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <YesNoDropdown label="Electrical Heating/Cooling" name="electrical_heating_cooling" value={formData.electrical_heating_cooling} onChange={handleChange} focusRing="red" />
                          <CustomDropdownField label="Calibration Table" fieldName="calibration_table" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          {/* <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Calibration Table Tank No</label><input type="text" name="calibration_table_tank_no" value={formData.calibration_table_tank_no} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" /></div> */}
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Manlid with Swingbolts</label><input type="text" name="manlid_type" value={formData.manlid_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" /></div>
                          <YesNoDropdown label="Collapsable Handrails" name="collapsable_handrails" value={formData.collapsable_handrails} onChange={handleChange} focusRing="red" />
                          <CustomDropdownField label="Dipstick" fieldName="dipstick" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <CustomDropdownField label="Topcovers Lockings" fieldName="topcovers_lockings" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <div><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Walkway</label><input type="text" name="walkway_type" value={formData.walkway_type} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" /></div>
                          <CustomDropdownField label="Manlid Gasket" fieldName="manlid_gasket" ringColor="red" formData={formData} customOptions={customOptions} showAddOption={showAddOption} newOptionText={newOptionText} handleDropdownChange={handleDropdownChange} setNewOptionText={setNewOptionText} handleAddCustomOption={handleAddCustomOption} setShowAddOption={setShowAddOption} />
                          <YesNoDropdown label="Grounding Lug" name="grounding_lug" value={formData.grounding_lug} onChange={handleChange} focusRing="red" />
                          <YesNoDropdown label="Bottom Plate" name="bottom_plate" value={formData.bottom_plate} onChange={handleChange} focusRing="red" />
                          {/* <div className="sm:col-span-3"><label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Frame/Cladding Details</label><textarea name="frame_cladding_details" value={formData.frame_cladding_details} onChange={handleChange} placeholder="Enter frame/cladding details" rows="3" className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" /></div> */}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-4 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-buttonBg text-buttonTxt
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:bg-transparent hover:text-buttonBg
                      "
                    >
                      {/* COLLAPSING BACKGROUND */}
                    <span
                      className="
                        absolute inset-0
                        bg-buttonBg
                        scale-x-100
                        group-hover:scale-x-0
                        origin-center
                        transition-transform duration-300 ease-out
                        z-0
                      "
                    />

                    <span className="relative z-10">Save OnHire Record</span>
                      {loading ? 'Saving...' : ''}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/onhires')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />

                      <span className="relative z-10">Cancel</span>
                    </button>

                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOnHire;
